import { useRef, useState } from "react";
import { uploadImage } from "@/api/upload";

interface ImageUploaderProps {
  value?: string;
  onChange?: (url: string | undefined) => void;
  placeholder: string;
  height?: number;
  brandColor?: string;
  /**
   * 为 true 时走服务端 `POST .../home/upload/image`（见 `api/upload`），
   * 为 false 时仅本地 `blob:` 预览，不上传。
   * @default true
   */
  remote?: boolean;
}

function revokeIfBlob(url: string | undefined) {
  if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
}

/**
 * 使用隐藏 file + 按钮 click() 触发选择，避免 opacity:0 覆盖在部分 WebView/浏览器中无法弹出文件框。
 */
export function ImageUploader({
  value,
  onChange,
  placeholder,
  height = 90,
  brandColor = "#3B82F6",
  remote = true,
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const openPicker = () => {
    if (uploading) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!remote) {
      const url = URL.createObjectURL(file);
      onChange?.(url);
      return;
    }

    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange?.(url);
    } catch {
      // uploadImage 内已 toast
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (uploading) return;
    revokeIfBlob(value);
    onChange?.(undefined);
  };

  return (
    <div className="relative" style={{ width: "100%", height }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        disabled={uploading}
        onChange={handleFileChange}
        className="sr-only"
        aria-hidden
        tabIndex={-1}
      />
      {value ? (
        <div
          className="relative overflow-hidden rounded-xl"
          style={{ width: "100%", height }}
        >
          <img
            src={value}
            alt=""
            className="pointer-events-none h-full w-full object-cover"
          />
          {uploading && (
            <div
              className="absolute inset-0 z-[15] flex items-center justify-center bg-black/35"
              aria-busy
              aria-label="上传中"
            >
              <i
                className="ri-loader-4-line animate-spin text-white"
                style={{ fontSize: 28 }}
              />
            </div>
          )}
          <button
            type="button"
            onClick={openPicker}
            disabled={uploading}
            className="absolute inset-0 z-[1] m-0 cursor-pointer border-0 bg-transparent p-0 disabled:cursor-wait"
            aria-label="重新选择图片"
          />
          <button
            type="button"
            onClick={handleDelete}
            disabled={uploading}
            className="absolute right-1.5 top-1.5 z-[20] flex h-6 w-6 items-center justify-center rounded-full bg-black/55"
            aria-label="删除图片"
          >
            <i className="ri-close-line text-white" style={{ fontSize: 12 }} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          disabled={uploading}
          className="relative m-0 block w-full cursor-pointer border-0 p-0 disabled:cursor-wait"
          style={{
            height,
            width: "100%",
            background: "#F8F9FC",
            border: "1.5px dashed #D1D5DB",
            borderRadius: 12,
          }}
          aria-label="上传图片"
        >
          {uploading && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-[inherit] bg-white/80"
              aria-busy
              aria-label="上传中"
            >
              <i
                className="ri-loader-4-line animate-spin"
                style={{ color: brandColor, fontSize: 26 }}
              />
            </div>
          )}
          <div
            className="pointer-events-none absolute inset-0 z-0 flex flex-col items-center justify-center px-2"
            aria-hidden
          >
            <i
              className="ri-camera-line"
              style={{ color: "#C0C4CC", fontSize: 22 }}
            />
            <span className="mt-1 max-w-full text-center text-xs leading-tight text-gray-400">
              {placeholder.split("\n").map((line, i) => (
                <span key={i}>
                  {line}
                  {i < placeholder.split("\n").length - 1 && <br />}
                </span>
              ))}
            </span>
          </div>
        </button>
      )}
    </div>
  );
}
